import { useState, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { brand, success } from "../../../theme/themePrimitives";
import { FormField } from "../../../components/FormField/FormField";
import { getShadeColour } from "../../../utils/helpers";
import { useCustomisation } from "../../../context/CustomisationContext";

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdatePassword?: (
    currentPassword: string,
    newPassword: string
  ) => Promise<void>;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordVisibility {
  currentPassword: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

interface PasswordErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

interface PasswordRequirement {
  key: string;
  label: string;
  validator: (password: string) => boolean;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  onClose,
  onUpdatePassword,
}) => {
  const [formValues, setFormValues] = useState<PasswordFormValues>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [visibility, setVisibility] = useState<PasswordVisibility>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [loading, setLoading] = useState(false);

  const { customisations } = useCustomisation();

  const {
    PROFILE: { buttonsSettings: profilebuttonssettings },
    GPC: { buttonsSettings: gpcbuttonsettings },
  } = customisations.customisations;
  const secondaryButtonTextColor =
    profilebuttonssettings?.profileBtnSecondaryTextColor ??
    gpcbuttonsettings?.gpcBtnSecondaryTextColor;
  const secondaryButtonBgColor =
    profilebuttonssettings?.profileBtnSecondaryBgColor ??
    gpcbuttonsettings?.gpcBtnSecondaryBgColor;

  // Password requirements configuration
  const passwordRequirements: PasswordRequirement[] = useMemo(
    () => [
      {
        key: "minLength",
        label: "Must be at least 8 characters",
        validator: (password) => password.length >= 8,
      },
      {
        key: "lowercase",
        label: "Must contain one lowercase letter",
        validator: (password) => /[a-z]/.test(password),
      },
      {
        key: "uppercase",
        label: "Must contain one Uppercase letter",
        validator: (password) => /[A-Z]/.test(password),
      },
      {
        key: "specialChar",
        label: "Must contain special characters: !@#$%*",
        validator: (password) => /[!@#$%*]/.test(password),
      },
    ],
    []
  );

  // Field configurations
  const fields = useMemo(
    () => [
      {
        key: "currentPassword" as const,
        label: "Current Password",
        placeholder: "Enter your current password",
        required: true,
      },
      {
        key: "newPassword" as const,
        label: "New Password",
        placeholder: "Enter your new password",
        required: true,
      },
      {
        key: "confirmPassword" as const,
        label: "Confirm new Password",
        placeholder: "Confirm your new password",
        required: true,
      },
    ],
    []
  );

  const handleFieldChange = (
    field: keyof PasswordFormValues,
    value: string
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleVisibility = (field: keyof PasswordVisibility) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleClose = () => {
    setFormValues({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setVisibility({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: PasswordErrors = {};

    // Validate current password
    if (!formValues.currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required";
    }

    // Validate new password
    if (!formValues.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else {
      const allRequirementsMet = passwordRequirements.every((req) =>
        req.validator(formValues.newPassword)
      );
      if (!allRequirementsMet) {
        newErrors.newPassword = "Password must meet all requirements";
      }
    }

    // Validate confirm password
    if (!formValues.confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (formValues.newPassword !== formValues.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleUpdatePassword = async () => {
    if (!validateForm()) {
      return;
    }

    if (onUpdatePassword) {
      setLoading(true);
      try {
        await onUpdatePassword(
          formValues.currentPassword,
          formValues.newPassword
        );
        handleClose();
      } catch (error) {
        console.error("Error updating password:", error);
      } finally {
        setLoading(false);
      }
    } else {
      handleClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "500px",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 1,
              }}
            >
              Reset password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your current password to change your password.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: "16px",
              right: "16px",
              border: "none",
            }}
          >
            <CloseIcon sx={{ color: "#98A2B3" }} />
          </IconButton>
        </Box>

        {/* Form Fields */}
        <Stack spacing={2} sx={{ mb: 3 }}>
          {fields.map((field) => (
            <FormField
              key={field.key}
              label={
                <>
                  {field.label}
                  {field.required && (
                    <Box component="span" sx={{ color: `var(--error-700)` }}>
                      {" *"}
                    </Box>
                  )}
                </>
              }
              type={visibility[field.key] ? "text" : "password"}
              value={formValues[field.key]}
              onChange={(value) => handleFieldChange(field.key, value)}
              placeholder={field.placeholder}
              error={!!errors[field.key]}
              helperText={errors[field.key]}
              endAdornment={
                <IconButton
                  onClick={() => toggleVisibility(field.key)}
                  edge="end"
                  size="small"
                  sx={{
                    border: "none !important",
                    bgcolor: "transparent !important",
                  }}
                >
                  {visibility[field.key] ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              }
            />
          ))}
        </Stack>

        {/* Password Requirements */}
        <Box sx={{ mb: 3 }}>
          <Stack spacing={1}>
            {passwordRequirements.map((requirement) => {
              const isValid = requirement.validator(formValues.newPassword);
              return (
                <Stack
                  key={requirement.key}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <CheckCircleRoundedIcon
                    sx={{
                      color: isValid ? success[600] : "#D0D5DD",
                      fontSize: "20px",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {requirement.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              px: 2,
              py: 1,
              border: "1px solid #D0D5DD",
              borderRadius: "8px",
              backgroundColor: "white !important",
              color: "text.primary",
              "&:hover": {
                bgcolor: `${getShadeColour("#ffffff"!, 5)} !important`,
                fontWeight: 700,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdatePassword}
            disabled={loading}
            sx={{
              px: 2,
              py: 1,
              borderRadius: "8px",
              backgroundColor: `${secondaryButtonBgColor} !important`,
              color: secondaryButtonTextColor,
              "&:hover": {
                backgroundColor: `${brand[700]} !important`,
              },
              "&:disabled": {
                backgroundColor: `${brand[400]} !important`,
              },
            }}
          >
            Update password
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
